import React, { FC } from 'react'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import DocumentVerificationIcon from 'components/DocumentVerificationIcon'
import { VERIFICATION_UNAVAILABLE_MESSAGE } from 'utils/documentVerificationHelpers'
import { DocumentVerificationStatus } from 'typescript/enums/documentVerification'
import { VerificationSeverity } from 'typescript/interfaces/documentVerification'

const LABELED_ICON_SIZE = 18
const LABELED_SPINNER_SIZE = 16
const COMPACT_ICON_SIZE = 16
const COMPACT_SPINNER_SIZE = 14

interface IStatusDisplay {
  label: string
  description?: string
  severity?: VerificationSeverity
}

const statusDisplays: Partial<Record<DocumentVerificationStatus, IStatusDisplay>> = {
  [DocumentVerificationStatus.pending]: {
    label: 'Verifying uploaded documents...',
  },
  [DocumentVerificationStatus.clean]: {
    label: 'Documents verified',
    description: 'Client data matches the uploaded documents',
    severity: 'success',
  },
  [DocumentVerificationStatus.warnings]: {
    label: 'Verification warnings',
    description: 'Some client data needs a manual check against the uploaded documents',
    severity: 'warning',
  },
  [DocumentVerificationStatus.errors]: {
    label: 'Verification errors',
    description: "Uploaded documents don't match the client data",
    severity: 'error',
  },
  [DocumentVerificationStatus.unavailable]: {
    label: 'Verification unavailable',
    description: VERIFICATION_UNAVAILABLE_MESSAGE,
    severity: 'info',
  },
}

interface IDocumentVerificationIndicatorProps {
  status?: DocumentVerificationStatus
  showLabel?: boolean
}

const DocumentVerificationIndicator: FC<IDocumentVerificationIndicatorProps> = ({ status, showLabel = false }) => {
  const display = status ? statusDisplays[status] : undefined

  if (!display || (!showLabel && status === DocumentVerificationStatus.clean)) return null

  const renderIcon = (iconSize: number, spinnerSize: number) =>
    display.severity ? (
      <DocumentVerificationIcon severity={display.severity} size={iconSize} />
    ) : (
      <CircularProgress size={spinnerSize} color="primary" />
    )

  if (showLabel)
    return (
      <Tooltip title={display.description ?? ''}>
        <Box display="flex" alignItems="center">
          {renderIcon(LABELED_ICON_SIZE, LABELED_SPINNER_SIZE)}
          <Box ml="0.5rem">
            <Typography variant="body1" color="textPrimary">
              {display.label}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    )

  return (
    <Tooltip title={display.description ?? display.label}>
      <Box display="inline-flex" alignItems="center" ml="0.5rem">
        {renderIcon(COMPACT_ICON_SIZE, COMPACT_SPINNER_SIZE)}
      </Box>
    </Tooltip>
  )
}

export default DocumentVerificationIndicator
